// frontend/src/pages/WhatsNewPage.jsx
// Full page showing all release notes history with rich text support
// Fetches current version dynamically from the API

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import secureAxios from "../utils/secureAxios";
import RichTextDisplay from "../components/common/RichTextDisplay";
import "../assets/css/rich-text-editor.css";

const WhatsNewPage = () => {
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedVersion, setSelectedVersion] = useState("all");
  const [availableVersions, setAvailableVersions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Fetch current version on mount
  useEffect(() => {
    fetchCurrentVersion();
    fetchAvailableVersions();
  }, []);

  // Fetch release notes when filters change
  useEffect(() => {
    fetchReleaseNotes();
  }, [selectedType, selectedVersion, pagination.page]);

  const fetchCurrentVersion = async () => {
    try {
      const response = await secureAxios.get("/api/version");
      setCurrentVersion(response.data.current_version || "1.0.0");
    } catch (err) {
      console.error("Error fetching current version:", err);
      setCurrentVersion("Unknown");
    }
  };

  const fetchReleaseNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (selectedType !== "all") {
        params.type = selectedType;
      }
      if (selectedVersion !== "all") {
        params.version = selectedVersion;
      }

      const response = await secureAxios.get("/api/announcements/release-notes/all", { params });
      setReleaseNotes(response.data.announcements || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 0
      }));
    } catch (err) {
      console.error("Error fetching release notes:", err);
      setError("Failed to load release notes");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVersions = async () => {
    try {
      const response = await secureAxios.get("/api/announcements/versions");
      setAvailableVersions(response.data.versions || []);
    } catch (err) {
      console.error("Error fetching versions:", err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "feature":
        return "bi-star-fill text-warning";
      case "bugfix":
        return "bi-bug-fill text-danger";
      case "maintenance":
        return "bi-tools text-info";
      case "release_notes":
        return "bi-journal-text text-primary";
      default:
        return "bi-megaphone text-secondary";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "feature":
        return "New Feature";
      case "bugfix":
        return "Bug Fix";
      case "maintenance":
        return "Maintenance";
      case "release_notes":
        return "Release Notes";
      default:
        return "General";
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case "feature":
        return "bg-warning text-dark";
      case "bugfix":
        return "bg-danger";
      case "maintenance":
        return "bg-info text-dark";
      case "release_notes":
        return "bg-primary";
      default:
        return "bg-secondary";
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "urgent":
        return "bg-danger";
      case "high":
        return "bg-warning text-dark";
      default:
        return null;
    }
  };

  // Group release notes by version
  const groupedByVersion = releaseNotes.reduce((acc, note) => {
    const version = note.version || "Unversioned";
    if (!acc[version]) {
      acc[version] = [];
    }
    acc[version].push(note);
    return acc;
  }, {});

  const announcementTypes = [
    { value: "all", label: "All Types" },
    { value: "release_notes", label: "Release Notes" },
    { value: "feature", label: "New Features" },
    { value: "bugfix", label: "Bug Fixes" },
    { value: "maintenance", label: "Maintenance" },
    { value: "general", label: "General" }
  ];

  return (
    <div className="container my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-stars me-2"></i>
            What's New
          </h2>
          <p className="text-muted mb-0">
            Release notes and updates for WebVenly
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-primary fs-6">
            Current: v{currentVersion || "..."}
          </span>
          <Link to="/crm" className="btn btn-outline-secondary">
            <i className="bi bi-arrow-left me-1"></i>
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label small text-muted">Filter by Type</label>
              <select
                className="form-select"
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                {announcementTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small text-muted">Filter by Version</label>
              <select
                className="form-select"
                value={selectedVersion}
                onChange={(e) => {
                  setSelectedVersion(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="all">All Versions</option>
                {availableVersions.map(v => (
                  <option key={v} value={v}>
                    {v} {v === currentVersion ? "(Current)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  setSelectedType("all");
                  setSelectedVersion("all");
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading release notes...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button className="btn btn-link" onClick={fetchReleaseNotes}>
            Try again
          </button>
        </div>
      ) : releaseNotes.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-journal-x display-4 text-muted"></i>
            <p className="mt-3 text-muted mb-0">
              No release notes found matching your filters
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Release notes grouped by version */}
          {Object.entries(groupedByVersion).map(([version, notes]) => (
            <div key={version} className="mb-4">
              {/* Version header */}
              <div className="d-flex align-items-center mb-3">
                <h4 className="mb-0 me-3">
                  <i className="bi bi-tag me-2"></i>
                  Version {version}
                </h4>
                {version === currentVersion && (
                  <span className="badge bg-success">Current</span>
                )}
              </div>

              {/* Notes for this version */}
              <div className="card">
                <div className="list-group list-group-flush">
                  {notes.map((note, index) => (
                    <div 
                      key={note.announcement_id} 
                      className={`list-group-item ${note.user_status !== 'viewed' && note.user_status !== 'dismissed' ? 'bg-light' : ''}`}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          {/* Type and priority badges */}
                          <div className="mb-2">
                            <span className={`badge ${getTypeBadgeClass(note.announcement_type)} me-2`}>
                              <i className={`bi ${getTypeIcon(note.announcement_type)} me-1`}></i>
                              {getTypeLabel(note.announcement_type)}
                            </span>
                            {getPriorityBadge(note.priority) && (
                              <span className={`badge ${getPriorityBadge(note.priority)}`}>
                                {note.priority}
                              </span>
                            )}
                            {note.user_status !== 'viewed' && note.user_status !== 'dismissed' && (
                              <span className="badge bg-primary ms-2">New</span>
                            )}
                          </div>

                          {/* Title */}
                          <h5 className="mb-2">{note.title}</h5>

                          {/* Content - Now using RichTextDisplay */}
                          <div className="mb-2">
                            <RichTextDisplay content={note.content} />
                          </div>

                          {/* Meta info */}
                          <small className="text-muted">
                            <i className="bi bi-calendar me-1"></i>
                            {new Date(note.created_at).toLocaleDateString()}
                            {note.created_by_name && (
                              <>
                                <span className="mx-2">|</span>
                                <i className="bi bi-person me-1"></i>
                                {note.created_by_name}
                              </>
                            )}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {[...Array(pagination.totalPages)].map((_, i) => (
                  <li 
                    key={i + 1} 
                    className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default WhatsNewPage;